import React, { useCallback, useEffect, useMemo, type ReactElement, type ReactNode } from 'react';
import { styled as styled2 } from '@compiled/react';
// eslint-disable-next-line jira/restricted/styled-components-migration, @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import styled from 'styled-components';
import { ErrorMessage, Field, type FieldProps } from '@atlaskit/form';
import Select from '@atlaskit/select';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import {
	SERVICE_DESK_PROJECT,
	CUSTOMER_SERVICE_PROJECT,
} from '@atlassian/jira-common-constants/src/project-types.tsx';
import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { getErrorType } from '@atlassian/jira-forge-ui-analytics/src/common/utils/get-error-type';
import { useIntl } from '@atlassian/jira-intl';
import {
	ErrorBoundaryBypassInput,
	ErrorBoundaryBypassOutput,
	ErrorBoundaryBypassProvider,
} from '@atlassian/jira-issue-adjustments/src/ui/error-boundary-bypass/index.tsx';
import { FieldBusyIndicator } from '@atlassian/jira-issue-adjustments/src/ui/field-busy-indicator/index.tsx';
import { IssueAdjustmentsErrorHandler } from '@atlassian/jira-issue-adjustments/src/ui/uim-root-async/error-handler/index.tsx';
import {
	ISSUE_TYPE,
	REQUEST_TYPE,
} from '@atlassian/jira-issue-create-common-types/src/common/constants/graphql-field-types.tsx';
import type {
	IssueType,
	RequestType,
} from '@atlassian/jira-issue-create-common-types/src/common/types/index.tsx';
import { FieldMessage } from '@atlassian/jira-issue-create-commons/src/common/ui/field-message/index.tsx';
import { FieldContainer } from '@atlassian/jira-issue-create-commons/src/common/ui/fields/styled.tsx';
import { useFormContext } from '@atlassian/jira-issue-create-form-context';
import { formatOptionLabel } from '@atlassian/jira-issue-field-select-base/src/ui/format-option-label/index.tsx';
import { FormApiProvider } from '@atlassian/jira-issue-form-api/src/controllers/index.tsx';
import { ISSUE_TYPE as ISSUE_TYPE_FIELDTYPE } from '@atlassian/jira-platform-field-config';
import { usePrevious } from '@atlassian/jira-platform-react-hooks-use-previous/src/common/utils/index.tsx';
import {
	fireTrackAnalytics,
	fireUIAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { GIC_VIEW_TYPE } from '@atlassian/jira-ui-modifications-view-gic/src/common/constants';
import { FieldDecoratorAsync } from '@atlassian/jira-ui-modifications-view-gic/src/ui/field-decorator/index.tsx';
import { FieldValueDecoratorAsync } from '@atlassian/jira-ui-modifications-view-gic/src/ui/field-value-decorator/index.tsx';
import { preventEscKeyPropagationKeyPressHandler, scrollToElement } from '../../../../common/utils';
import { openInNewTabLinkClickEventHandler } from '../../../../common/utils/link-handler/index.tsx';
import { ISSUE_TYPE_HELP_TEXT_URL, ISSUE_TYPE_PICKER_ELEMENT_ID } from './constants';
import messages from './messages';
import { WhatsThis } from './request-type-changeboarding';
import { RequestTypeHelpText } from './request-type-help-text';
import { RequestTypeSelect } from './request-type-select';
import type { NoneOption } from './request-type-select/types';
import type { Props, PropsFromUIM } from './types';
import {
	getFieldLabel,
	getFirstMatchingRequestType,
	getIssueTypeAndRequestType,
	getRequestAndIssueTypeOptionsGroup,
} from './utils';

const typePickerMargin = gridSize * 2.25;

const TypePicker = (props: Props) => {
	const { formatMessage } = useIntl();
	const {
		onChange,
		issueTypes,
		requestTypes,
		value: selectedRequestOrIssueType,
		projectType,
		isDisabled = false,
		isMini,
	} = props;

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const previousSelectedRequestOrIssueType = usePrevious(selectedRequestOrIssueType);

	useEffect(() => {
		// if the default selection has request type of "none"
		// then we will change it to select the first matching request type
		if (
			// we have request type data
			requestTypes &&
			// previously data was loading
			previousSelectedRequestOrIssueType === null &&
			// an issue type is selected
			// i.e. this is the default selection
			// and it has a "none" request type
			selectedRequestOrIssueType &&
			selectedRequestOrIssueType.type === ISSUE_TYPE
		) {
			const firstMatchingRequestType = getFirstMatchingRequestType({
				issueType: selectedRequestOrIssueType,
				allRequestTypes: requestTypes,
			});
			if (firstMatchingRequestType) {
				onChange(firstMatchingRequestType);
				fireTrackAnalytics(createAnalyticsEvent({}), 'gicRequestTypeSelect forceDefaultNotNone');
			}
		}
	}, [
		createAnalyticsEvent,
		onChange,
		previousSelectedRequestOrIssueType,
		requestTypes,
		selectedRequestOrIssueType,
	]);

	const getIssueTypeHelpText = useCallback(
		() => (
			<FieldMessage
				fieldHelpTextUrl={ISSUE_TYPE_HELP_TEXT_URL}
				fieldName="Issue Type"
				onLinkClickEvent={openInNewTabLinkClickEventHandler}
				isMini={isMini}
			/>
		),
		[isMini],
	);

	const [
		{
			meta: { submitFieldwiseErrors },
		},
	] = useFormContext();

	useEffect(() => {
		if (submitFieldwiseErrors?.issuetype) {
			scrollToElement(ISSUE_TYPE_PICKER_ELEMENT_ID);
		}
	}, [submitFieldwiseErrors?.issuetype]);

	const fields = useMemo(() => {
		let fieldsNode: ReactElement;

		const projectTypeCheck = fg('jcs_master_flag')
			? projectType === SERVICE_DESK_PROJECT || projectType === CUSTOMER_SERVICE_PROJECT
			: projectType === SERVICE_DESK_PROJECT;
		if (projectTypeCheck && requestTypes !== undefined) {
			// get the options for the 2 select fields
			const { issueTypeOptions, requestTypeOptions } = getRequestAndIssueTypeOptionsGroup(
				issueTypes,
				requestTypes,
				formatMessage,
			);

			// extract the selected option for the 2 select fields from the aggregated value
			const { issueType: selectedIssueType, requestType: selectedRequestType } =
				getIssueTypeAndRequestType({
					allIssueTypes: issueTypeOptions,
					requestOrIssueType: selectedRequestOrIssueType,
					formatMessage,
				});

			// fire UI events when the selects are opened
			const onIssueTypeSelectOpen = () =>
				fireUIAnalytics(createAnalyticsEvent({}), 'gicIssueTypeSelect open');
			const onRequestTypeSelectOpen = () =>
				fireUIAnalytics(createAnalyticsEvent({}), 'gicRequestTypeSelect open');

			// on issue type selection we want to pre-select a matching request type
			const onIssueTypeChange = (issueType: IssueType) => {
				if (issueType.value === selectedIssueType?.value) {
					return;
				}
				const firstMatchingRequestType = getFirstMatchingRequestType({
					issueType,
					allRequestTypes: requestTypes,
				});
				onChange(firstMatchingRequestType || issueType);
				fireUIAnalytics(createAnalyticsEvent({}), 'gicIssueTypeSelect selected');
			};

			// on request type selection we want to select the chosen request type,
			// or if 'none' is selected, we select the issue type
			const onRequestTypeChange = (requestTypeOrNone: RequestType | NoneOption) => {
				if (requestTypeOrNone.value === selectedRequestType?.value) {
					return;
				}
				let isNoneSelected = true;
				if (requestTypeOrNone.type === REQUEST_TYPE) {
					onChange(requestTypeOrNone);
					isNoneSelected = false;
				} else if (selectedIssueType !== null) {
					onChange(selectedIssueType);
				}
				fireUIAnalytics(createAnalyticsEvent({}), 'gicRequestTypeSelect selected', {
					isNoneSelected,
				});
			};

			const requestTypeHelpText =
				selectedRequestType && selectedRequestType.type === REQUEST_TYPE
					? selectedRequestType.helpText
					: '';
			fieldsNode = (
				<>
					<FieldContainer width={isMini ? '100%' : undefined} marginTop={typePickerMargin}>
						<Field
							name="issue-type-picker"
							label={formatMessage(messages.issueTypeFieldLabel)}
							isRequired
						>
							{({ fieldProps: { id, isRequired } }) => (
								<>
									<Select
										inputId={id}
										required={isRequired}
										options={issueTypeOptions}
										value={selectedIssueType}
										// @ts-expect-error - TS2322 - Type '(issueType: IssueType) => void' is not assignable to type '(value: Option | null, action: ActionMeta<Option>) => void'.
										onChange={onIssueTypeChange}
										onMenuOpen={onIssueTypeSelectOpen}
										isClearable={false}
										formatOptionLabel={formatOptionLabel}
										placeholder=""
										id={ISSUE_TYPE_PICKER_ELEMENT_ID}
										isDisabled={isDisabled}
										onKeyDown={preventEscKeyPropagationKeyPressHandler}
									/>
									{getIssueTypeHelpText()}
									{submitFieldwiseErrors?.issuetype && (
										<ErrorMessage>{submitFieldwiseErrors.issuetype}</ErrorMessage>
									)}
								</>
							)}
						</Field>
					</FieldContainer>
					<FieldContainer width="100%" marginTop={typePickerMargin}>
						<RequestTypeFieldWrapper>
							<Field
								name="request-type-picker"
								label={
									<RequestTypeFieldLabelWrapper>
										<div>
											{formatMessage(messages.requestTypeFieldLabel)}
											<RequiredIndicator>*</RequiredIndicator>
										</div>
										<WhatsThis />
									</RequestTypeFieldLabelWrapper>
								}
							>
								{({ fieldProps: { id } }) => (
									<RequestTypeSelect
										inputId={id}
										options={requestTypeOptions}
										value={selectedRequestType}
										onChange={onRequestTypeChange}
										onMenuOpen={onRequestTypeSelectOpen}
										// id for cypress testing
										id="issue-create.ui.modal.create-form.type-picker.request-type-select"
									/>
								)}
							</Field>
						</RequestTypeFieldWrapper>
						{requestTypeHelpText && <RequestTypeHelpText helpText={requestTypeHelpText} />}
					</FieldContainer>
				</>
			);
		} else {
			// there is no request type data, just show issue type select
			const label = getFieldLabel(Boolean(requestTypes?.length), formatMessage);

			const onIssueTypeChange = (selectedIssueType: IssueType): void => {
				if (selectedIssueType.value === selectedRequestOrIssueType?.value) {
					return;
				}
				const analyticsEvent = createAnalyticsEvent({
					action: 'selected',
					actionSubject: 'option',
				});
				fireUIAnalytics(analyticsEvent, 'issueType');
				onChange(selectedIssueType);
			};

			const Component = (componentProps: PropsFromUIM) => (
				<JSErrorBoundary
					id="withFormField"
					packageName="jiraIssueCreateModal"
					sendToPrivacyUnsafeSplunk
				>
					<Field
						name="type-picker"
						label={label}
						isRequired
						elementAfterLabel={
							componentProps.areUiModificationsBusy ? (
								<FieldBusyIndicator fieldId={ISSUE_TYPE_FIELDTYPE} />
							) : undefined
						}
					>
						{/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
						{({ fieldProps }: { fieldProps: FieldProps<any, HTMLInputElement> }) => {
							const renderFieldContent = ({
								id,
								isRequired, // eslint-disable-next-line @typescript-eslint/no-explicit-any
							}: FieldProps<any, HTMLInputElement>) => (
								<>
									<Select
										inputId={id}
										required={isRequired}
										options={componentProps.allowedValues}
										// @ts-expect-error - TS2322 - Type '(selectedIssueType: IssueType) => void' is not assignable to type '(value: Option | null, action: ActionMeta<Option>) => void'.
										onChange={onIssueTypeChange}
										value={selectedRequestOrIssueType}
										isClearable={false}
										formatOptionLabel={formatOptionLabel}
										placeholder=""
										id={ISSUE_TYPE_PICKER_ELEMENT_ID}
										isDisabled={!!(isDisabled || componentProps.isReadOnly)}
										onKeyDown={preventEscKeyPropagationKeyPressHandler}
									/>
									{getIssueTypeHelpText()}
									{submitFieldwiseErrors?.issuetype && (
										<ErrorMessage>{submitFieldwiseErrors.issuetype}</ErrorMessage>
									)}
								</>
							);

							return (
								<ErrorBoundaryBypassProvider>
									<ErrorBoundaryBypassOutput />
									<JSErrorBoundary
										id="issueAdjustmentsFieldValueDecorator"
										packageName="jiraIssueCreateModal"
										fallback={() => (
											<>
												<IssueAdjustmentsErrorHandler />
												{renderFieldContent(fieldProps)}
											</>
										)}
										sendToPrivacyUnsafeSplunk
										attributes={(error: Error) => ({
											errorType: getErrorType(error),
											viewType: GIC_VIEW_TYPE,
											fieldId: ISSUE_TYPE_FIELDTYPE,
											fieldType: ISSUE_TYPE_FIELDTYPE,
										})}
									>
										<FieldValueDecoratorAsync
											fieldId={ISSUE_TYPE_FIELDTYPE}
											fieldType={ISSUE_TYPE_FIELDTYPE}
											fieldProps={{
												...fieldProps,
												value: selectedRequestOrIssueType,
											}}
										>
											{(interceptedFieldProps) => (
												<ErrorBoundaryBypassInput>
													{renderFieldContent(
														// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
														interceptedFieldProps as FieldProps<
															// eslint-disable-next-line @typescript-eslint/no-explicit-any
															any,
															HTMLInputElement
														>,
													)}
												</ErrorBoundaryBypassInput>
											)}
										</FieldValueDecoratorAsync>
									</JSErrorBoundary>
								</ErrorBoundaryBypassProvider>
							);
						}}
					</Field>
				</JSErrorBoundary>
			);

			const typePickerComponentProps = {
				fieldId: ISSUE_TYPE_FIELDTYPE,
				fieldName: label,
				fieldType: ISSUE_TYPE_FIELDTYPE,
				isRequired: true,
				gqlType: '',
				allowedValues: issueTypes,
			};

			const setFieldValue = (_: string, value: unknown) =>
				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				onIssueTypeChange(value as IssueType);
			const getFormState = () => ({
				values: {
					[typePickerComponentProps.fieldId]: selectedRequestOrIssueType,
				},
			});

			fieldsNode = (
				<FormApiProvider setFieldValue={setFieldValue} getFormState={getFormState}>
					<FieldContainer width={isMini ? '100%' : undefined} marginTop={typePickerMargin}>
						<JSErrorBoundary
							id="globalIssueCreateIssueAdjustmentsAdapter"
							packageName="issueCreateModal"
							fallback={() => (
								<>
									<IssueAdjustmentsErrorHandler />
									<Component {...typePickerComponentProps} />
								</>
							)}
							sendToPrivacyUnsafeSplunk
							attributes={(error: Error) => ({
								errorType: getErrorType(error),
								viewType: GIC_VIEW_TYPE,
								fieldId: typePickerComponentProps.fieldId,
								fieldType: typePickerComponentProps.fieldType,
							})}
						>
							<FieldDecoratorAsync
								Component={Component}
								componentProps={typePickerComponentProps}
							/>
						</JSErrorBoundary>
					</FieldContainer>
				</FormApiProvider>
			);
		}

		return fieldsNode;
	}, [
		createAnalyticsEvent,
		formatMessage,
		issueTypes,
		onChange,
		projectType,
		requestTypes,
		selectedRequestOrIssueType,
		getIssueTypeHelpText,
		submitFieldwiseErrors?.issuetype,
		isDisabled,
		isMini,
	]);

	return fields;
};

export default TypePicker;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const RequestTypeFieldWrapper = styled.div<{
	children?: ReactNode;
}>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'> div': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'> label': {
			width: '100%',
		},
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const RequiredIndicator = styled2.span<{
	children?: ReactNode;
}>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text.danger', colors.R400),
	paddingLeft: token('space.025', '2px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const RequestTypeFieldLabelWrapper = styled2.div<{ children?: ReactNode }>({
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'baseline',
	justifyContent: 'space-between',
	width: '100%',
	height: '100%',
	marginTop: token('space.075', '6px'),
});
